import { createSelector } from 'reselect';

import {
  DataKiSyokenResponse,
  DataKiSyokenSearchParams,
} from '../../domain/kiSyoken/types';
import { KiTagListOptions, LoadingState } from '../../domain/schemas';

/* ---------------------------------------------------------------
 * Action Types
 */

const FETCH_DATA_KISYOKEN = 'FETCH_DATA_KISYOKEN' as const;
const FETCH_DATA_KISYOKEN_REQUEST = 'FETCH_DATA_KISYOKEN_REQUEST' as const;
const FETCH_DATA_KISYOKEN_SUCCESS = 'FETCH_DATA_KISYOKEN_SUCCESS' as const;
const RENEW_DATA_KISYOKEN = 'RENEW_DATA_KISYOKEN' as const;
const FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE =
  'FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE' as const;
const FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_REQUEST =
  'FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_REQUEST' as const;
const FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_SUCCESS =
  'FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_SUCCESS' as const;
const RESET_DATA_KISYOKEN_KI_LIST_AVERAGE =
  'RESET_DATA_KISYOKEN_KI_LIST_AVERAGE' as const;
const FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE =
  'FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE' as const;
const FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_REQUEST =
  'FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_REQUEST' as const;
const FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_SUCCESS =
  'FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_SUCCESS' as const;
const FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE =
  'FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE' as const;
const FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_REQUEST =
  'FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_REQUEST' as const;
const FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_SUCCESS =
  'FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_SUCCESS' as const;
const CLEAR_DATA_KISYOKEN_KI_TAG_AVERAGE =
  'CLEAR_DATA_KISYOKEN_KI_TAG_AVERAGE' as const;
const CLEAR_DATA_KISYOKEN_FILTERED_TAG_AVERAGE =
  'CLEAR_DATA_KISYOKEN_FILTERED_TAG_AVERAGE' as const;
const REMOVE_DATA_KISYOKEN_KI_TAG_AVERAGE_BY_ID =
  'REMOVE_DATA_KISYOKEN_KI_TAG_AVERAGE_BY_ID' as const;

export const DataKiSyokenActionTypes = {
  FETCH_DATA_KISYOKEN,
  FETCH_DATA_KISYOKEN_REQUEST,
  FETCH_DATA_KISYOKEN_SUCCESS,
  RENEW_DATA_KISYOKEN,
  FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE,
  FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_REQUEST,
  FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_SUCCESS,
  RESET_DATA_KISYOKEN_KI_LIST_AVERAGE,
  FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE,
  FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_REQUEST,
  FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_SUCCESS,
  FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE,
  FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_REQUEST,
  FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_SUCCESS,
  CLEAR_DATA_KISYOKEN_KI_TAG_AVERAGE,
  CLEAR_DATA_KISYOKEN_FILTERED_TAG_AVERAGE,
  REMOVE_DATA_KISYOKEN_KI_TAG_AVERAGE_BY_ID,
};

/* ---------------------------------------------------------------
 * Action Creators
 */

/**
 * dataKiSyokenのデータを取得する
 */
function fetchDataKiSyokenAction(params?: DataKiSyokenSearchParams) {
  return {
    type: FETCH_DATA_KISYOKEN,
    payload: { params },
  };
}

function fetchDataKiSyokenRequestAction() {
  return {
    type: FETCH_DATA_KISYOKEN_REQUEST,
  };
}

function fetchDataKiSyokenSuccessAction(dataKiSyoken: DataKiSyokenResponse) {
  return {
    type: FETCH_DATA_KISYOKEN_SUCCESS,
    payload: { dataKiSyoken },
  };
}

function renewDataKiSyokenAction() {
  return {
    type: RENEW_DATA_KISYOKEN,
  };
}

/**
 * [機種別商圏] チェック機種絞り込みの平均合計行
 * @param searchResultSetting
 * @param selectedKiList
 * @returns
 */
function fetchDataKiSyokenKiListAverageAction(
  params: DataKiSyokenSearchParams,
  selectedKiList: string[] = []
) {
  return {
    type: FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE,
    payload: {
      params: {
        ...params,
        kiList: selectedKiList,
      },
    },
  };
}

/**
 * [機種別商圏] チェック機種絞り込みの平均合計行のリクエスト
 */
function fetchDataKiSyokenKiListAverageRequestAction() {
  return {
    type: FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_REQUEST,
  };
}

/**
 * [機種別商圏] チェック機種絞り込みの平均合計行を登録
 * @param data
 * @returns
 */
function fetchDataKiSyokenKiListAverageSuccessAction(
  data: DataKiSyokenResponse
) {
  return {
    type: FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_SUCCESS,
    payload: { data },
  };
}

/**
 * [機種別商圏] チェック機種絞り込みの平均合計行をリセットする
 */
function resetDataKiSyokenKiListAverageAction() {
  return {
    type: RESET_DATA_KISYOKEN_KI_LIST_AVERAGE,
  };
}

/**
 * [機種別商圏] 選択されたタグに紐づく機種を登録（選択されたタグ全体平均行用）
 * @param searchResultSetting
 * @param tags
 * @returns
 */
function fetchDataKiSyokenFilteredKiTagAverageAction(
  dataKiSyoken: DataKiSyokenSearchParams,
  tagIds: KiTagListOptions[]
) {
  return {
    type: FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE,
    payload: {
      params: {
        ...dataKiSyoken,
        tagIds: tagIds.map((tag) => tag.tagId),
      },
    },
  };
}

/**
 * [機種別商圏] チェック機種絞り込みの平均合計行のリクエスト （選択されたタグ全体平均行用）
 */
function fetchDataKiSyokenFilteredKiTagAverageRequestAction() {
  return {
    type: FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_REQUEST,
  };
}

/**
 * 機種タグ設定 機種タグに紐づけられたデータを登録（選択されたタグ全体平均行用）
 * @param data
 * @returns
 */
function fetchDataKiSyokenFilteredKiTagAverageSuccessAction(
  data: DataKiSyokenResponse
) {
  return {
    type: FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_SUCCESS,
    payload: { data },
  };
}

/**
 * [機種別商圏] 選択されたタグに紐づく機種を登録（タグ毎の別平均行用）
 * @param searchResultSetting
 * @param selectedKiList
 * @param tagId
 * @returns
 */
function fetchDataKiSyokenKiTagAverageAction(
  dataKiSyoken: DataKiSyokenSearchParams,
  tagId: string
) {
  return {
    type: FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE,
    payload: {
      params: {
        ...dataKiSyoken,
        tagIds: [tagId],
      },
    },
  };
}

/**
 * [機種別商圏] タグ絞り込みの平均合計行のリクエスト
 */
function fetchDataKiSyokenKiTagAverageRequestAction() {
  return {
    type: FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_REQUEST,
  };
}

/**
 * 機種タグ設定 機種タグに紐づけられた全機種を登録（タグ別平均行用）
 * @param data
 * @returns
 */
function fetchDataKiSyokenKiTagAverageSuccessAction(
  data: DataKiSyokenResponse
) {
  return {
    type: FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_SUCCESS,
    payload: { data },
  };
}

/**
 * [機種別商圏] 機種タグ別の平均合計行をクリアする
 */
function clearDataKiSyokenKiTagAverageAction() {
  return {
    type: CLEAR_DATA_KISYOKEN_KI_TAG_AVERAGE,
  };
}

/**
 * [機種別商圏] 選択されたタグ全体の平均合計行をクリアする
 */
function clearDataKiSyokenFilteredTagAverageAction() {
  return {
    type: CLEAR_DATA_KISYOKEN_FILTERED_TAG_AVERAGE,
  };
}

/**
 * [機種別商圏] 特定のタグの平均合計行を削除する
 * @param tagId 削除するタグのID
 */
function removeDataKiSyokenKiTagAverageByIdAction(tagId: string) {
  return {
    type: REMOVE_DATA_KISYOKEN_KI_TAG_AVERAGE_BY_ID,
    payload: { tagId },
  };
}

export const DataKiSyokenActionCreators = {
  fetchDataKiSyokenAction,
  fetchDataKiSyokenRequestAction,
  fetchDataKiSyokenSuccessAction,
  renewDataKiSyokenAction,
  fetchDataKiSyokenKiListAverageAction,
  fetchDataKiSyokenKiListAverageRequestAction,
  fetchDataKiSyokenKiListAverageSuccessAction,
  resetDataKiSyokenKiListAverageAction,
  fetchDataKiSyokenKiTagAverageAction,
  fetchDataKiSyokenKiTagAverageRequestAction,
  fetchDataKiSyokenKiTagAverageSuccessAction,
  fetchDataKiSyokenFilteredKiTagAverageAction,
  fetchDataKiSyokenFilteredKiTagAverageRequestAction,
  fetchDataKiSyokenFilteredKiTagAverageSuccessAction,
  clearDataKiSyokenKiTagAverageAction,
  clearDataKiSyokenFilteredTagAverageAction,
  removeDataKiSyokenKiTagAverageByIdAction,
};

/* ---------------------------------------------------------------
 * Actions
 */

export type FetchDataKiSyokenAction = ReturnType<
  typeof fetchDataKiSyokenAction
>;
type FetchDataKiSyokenRequestAction = ReturnType<
  typeof fetchDataKiSyokenRequestAction
>;
type FetchDataKiSyokenSuccessAction = ReturnType<
  typeof fetchDataKiSyokenSuccessAction
>;
type RenewDataKiSyokenAction = ReturnType<typeof renewDataKiSyokenAction>;

export type FetchDataKiSyokenKiListAverageAction = ReturnType<
  typeof fetchDataKiSyokenKiListAverageAction
>;
export type FetchDataKiSyokenKiListAverageRequestAction = ReturnType<
  typeof fetchDataKiSyokenKiListAverageRequestAction
>;
export type FetchDataKiSyokenKiListAverageSuccessAction = ReturnType<
  typeof fetchDataKiSyokenKiListAverageSuccessAction
>;
export type ResetDataKiSyokenKiListAverageAction = ReturnType<
  typeof resetDataKiSyokenKiListAverageAction
>;
export type FetchDataKiSyokenKiTagAverageAction = ReturnType<
  typeof fetchDataKiSyokenKiTagAverageAction
>;
export type FetchDataKiSyokenKiTagAverageRequestAction = ReturnType<
  typeof fetchDataKiSyokenKiTagAverageRequestAction
>;
export type FetchDataKiSyokenKiTagAverageSuccessAction = ReturnType<
  typeof fetchDataKiSyokenKiTagAverageSuccessAction
>;

export type FetchDataKiSyokenFilteredKiTagAverageAction = ReturnType<
  typeof fetchDataKiSyokenFilteredKiTagAverageAction
>;
export type FetchDataKiSyokenFilteredKiTagAverageRequestAction = ReturnType<
  typeof fetchDataKiSyokenFilteredKiTagAverageRequestAction
>;
export type FetchDataKiSyokenFilteredKiTagAverageSuccessAction = ReturnType<
  typeof fetchDataKiSyokenFilteredKiTagAverageSuccessAction
>;

export type ClearDataKiSyokenKiTagAverageAction = ReturnType<
  typeof clearDataKiSyokenKiTagAverageAction
>;

export type ClearDataKiSyokenFilteredTagAverageAction = ReturnType<
  typeof clearDataKiSyokenFilteredTagAverageAction
>;

export type RemoveDataKiSyokenKiTagAverageByIdAction = ReturnType<
  typeof removeDataKiSyokenKiTagAverageByIdAction
>;

type DataKiSyokenAction =
  | FetchDataKiSyokenAction
  | FetchDataKiSyokenRequestAction
  | FetchDataKiSyokenSuccessAction
  | RenewDataKiSyokenAction
  | FetchDataKiSyokenKiListAverageAction
  | FetchDataKiSyokenKiListAverageRequestAction
  | FetchDataKiSyokenKiListAverageSuccessAction
  | ResetDataKiSyokenKiListAverageAction
  | FetchDataKiSyokenKiTagAverageAction
  | FetchDataKiSyokenKiTagAverageRequestAction
  | FetchDataKiSyokenKiTagAverageSuccessAction
  | FetchDataKiSyokenFilteredKiTagAverageAction
  | FetchDataKiSyokenFilteredKiTagAverageRequestAction
  | FetchDataKiSyokenFilteredKiTagAverageSuccessAction
  | ClearDataKiSyokenKiTagAverageAction
  | ClearDataKiSyokenFilteredTagAverageAction
  | RemoveDataKiSyokenKiTagAverageByIdAction;

/* ---------------------------------------------------------------
 * State
 */

type DataKiSyokenState = {
  loadingState: LoadingState;
  dataKiSyoken: DataKiSyokenResponse | undefined;
  isInitialLoad: boolean;
  KiListAverage: {
    loadingState: LoadingState;
    data: DataKiSyokenResponse | undefined;
  };
  kiTagFilterAverage: {
    loadingState: LoadingState;
    data: DataKiSyokenResponse | undefined;
  };
  kiTagsAverage: {
    loadingState: LoadingState;
    data: DataKiSyokenResponse[] | undefined;
    value: KiTagListOptions[];
  };
};

const initialState: DataKiSyokenState = {
  loadingState: 'prepare',
  dataKiSyoken: undefined,
  isInitialLoad: true,
  KiListAverage: {
    loadingState: 'prepare',
    data: undefined,
  },
  kiTagFilterAverage: {
    loadingState: 'prepare',
    data: undefined,
  },
  kiTagsAverage: {
    loadingState: 'prepare',
    data: undefined,
    value: [],
  },
};

/* ---------------------------------------------------------------
 * Selector
 */

/**
 * [機種別商圏] dataの全ての状態を取得する
 */
export function dataKiSyokenSelector(rootState: {
  dataKiSyoken: DataKiSyokenState;
}) {
  return rootState.dataKiSyoken;
}

/**
 * [機種別商圏] dataのローディング状態を取得する
 */
export const dataKiSyokenLoadingStateSelector = createSelector(
  dataKiSyokenSelector,
  ({ loadingState }) => loadingState
);

/**
 * [機種別商圏] dataのデータを取得する
 */
export const dataKiSyokenDataSelector = createSelector(
  dataKiSyokenSelector,
  ({ dataKiSyoken }) => dataKiSyoken
);

/**
 * [機種別商圏] 初回ロード状態を取得する
 */
export const dataKiSyokenIsInitialLoadSelector = createSelector(
  dataKiSyokenSelector,
  ({ isInitialLoad }) => isInitialLoad
);

/**
 * [機種別商圏] KiListAverageのデータを取得する
 */
export const dataKiSyokenKiListAverageDataSelector = createSelector(
  dataKiSyokenSelector,
  ({ KiListAverage }) => KiListAverage.data
);

/**
 * [機種別商圏] 機種タグ全体の平均行データを取得する（選択されたタグ全体平均行用）
 */
export const dataKiSyokenKiFilteredTagsAverageSelector = createSelector(
  dataKiSyokenSelector,
  // demo
  ({ kiTagFilterAverage }) => kiTagFilterAverage.data
);

/**
 * [機種別商圏] 機種タグの平均行データを取得する
 */
export const dataKiSyokenKiTagsAverageSelector = createSelector(
  dataKiSyokenSelector,
  // demo
  ({ kiTagsAverage }) => kiTagsAverage.data
);

/**
 * [機種別商圏] チェック機種絞り込み平均合計行のローディング状態を取得する
 */
export const dataKiSyokenKiListAverageIsLoadingSelector = createSelector(
  dataKiSyokenSelector,
  ({ KiListAverage }) => KiListAverage.loadingState === 'loading'
);

/**
 * [機種別商圏] 機種タグのローディング状態を取得する
 */
export const dataKiSyokenKiTagIsLoadingSelector = createSelector(
  dataKiSyokenSelector,
  ({ kiTagFilterAverage, kiTagsAverage }) =>
    kiTagFilterAverage.loadingState === 'loading' ||
    kiTagsAverage.loadingState === 'loading'
);

/* ---------------------------------------------------------------
 * Reducer
 */

export function dataKiSyokenReducer(
  state = initialState,
  action: DataKiSyokenAction
): DataKiSyokenState {
  switch (action.type) {
    case FETCH_DATA_KISYOKEN_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_DATA_KISYOKEN_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
        dataKiSyoken: action.payload.dataKiSyoken,
        isInitialLoad: false,
      };
    case RENEW_DATA_KISYOKEN:
      return initialState;
    case FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_REQUEST: {
      return {
        ...state,
        KiListAverage: {
          ...state.KiListAverage,
          loadingState: 'loading',
        },
      };
    }
    case FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE_SUCCESS: {
      return {
        ...state,
        KiListAverage: {
          loadingState: 'loaded',
          data: action.payload.data,
        },
      };
    }
    case RESET_DATA_KISYOKEN_KI_LIST_AVERAGE: {
      return {
        ...state,
        KiListAverage: initialState.KiListAverage,
      };
    }
    case FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_REQUEST: {
      return {
        ...state,
        kiTagsAverage: {
          ...state.kiTagsAverage,
          loadingState: 'loading',
        },
      };
    }
    case FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE_SUCCESS: {
      return {
        ...state,
        kiTagsAverage: {
          ...state.kiTagsAverage,
          loadingState: 'loaded',
          data: state.kiTagsAverage.data
            ? [...state.kiTagsAverage.data, action.payload.data]
            : [action.payload.data],
        },
      };
    }
    case FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_REQUEST: {
      return {
        ...state,
        kiTagFilterAverage: {
          ...state.kiTagFilterAverage,
          loadingState: 'loading',
        },
      };
    }
    case FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE_SUCCESS: {
      return {
        ...state,
        kiTagFilterAverage: {
          loadingState: 'loaded',
          data: action.payload.data,
        },
      };
    }
    case CLEAR_DATA_KISYOKEN_KI_TAG_AVERAGE: {
      return {
        ...state,
        kiTagsAverage: initialState.kiTagsAverage,
      };
    }
    case CLEAR_DATA_KISYOKEN_FILTERED_TAG_AVERAGE: {
      return {
        ...state,
        kiTagFilterAverage: initialState.kiTagFilterAverage,
      };
    }
    case REMOVE_DATA_KISYOKEN_KI_TAG_AVERAGE_BY_ID: {
      return {
        ...state,
        kiTagsAverage: {
          ...state.kiTagsAverage,
          data:
            state.kiTagsAverage.data?.filter((data) => {
              // setting.tagIdsに含まれるタグIDが削除するタグIDと一致しないものを保持
              return !data.setting.tagIds?.includes(action.payload.tagId);
            }) || [],
        },
      };
    }
    default:
      return state;
  }
}
